import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  h,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";

import {
  ChCheckboxCustomEvent,
  // ChEditCustomEvent,
  TreeViewItemContextMenu
} from "@genexus/chameleon-controls-library";
import { TreeViewItemModel } from "@genexus/chameleon-controls-library";
import { TreeViewItemModelExtended } from "@genexus/chameleon-controls-library";
import { getIconPath, MercuryBundles } from "@genexus/mercury";

import { Locale } from "../../common/locale";

import {
  ImportCategoryData,
  ImportItemResultData,
  ContextMenuInfo
} from "../../common/types";
import {
  convertImportCategoryDataToTreeView,
  addImportItemResultDataItem,
  convertImportCategoryItemToTreeItem
} from "./helpers";
import { CheckedItemsInfo } from "../_helpers/list-selector/list-selector";

import { KB_OBJECT, STATUS_REGEXP_KEY } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/checkbox",
  "components/edit",
  "components/icon",
  "components/tree-view",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing"
];

const NOTICE_ICON = getIconPath({
  category: "gemini-tools",
  name: "notice",
  colorType: "on-surface"
});

const FILE_ICON = getIconPath({
  category: "gemini-tools",
  name: "file",
  colorType: "primary"
});

const GEMINI_TOOLS_SETTINGS = getIconPath({
  category: "gemini-tools",
  name: "settings",
  colorType: "primary"
});

const MENU_DELETE = getIconPath({
  category: "menus",
  name: "delete",
  colorType: "primary"
});

const CHECKBOX_ALL_CHECKED_VALUE = "allChecked";
const CHECKBOX_ALL_UNCHECKED_VALUE = "allUnchecked";

@Component({
  tag: "gx-ide-kb-manager-import",
  styleUrl: "kb-manager-import.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/kb-manager-import"]
})
export class GxIdeKbManagerImport {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #selectedObjectsInFileIds: string[] = [];

  @Element() el: HTMLGxIdeKbManagerImportElement;

  #fileInputEl!: HTMLChEditElement;
  #fileInputHiddenEl!: HTMLInputElement;
  #objectsTreeEl!: HTMLChTreeViewRenderElement;
  #successItemsIds: Set<string> = new Set();
  #warningItemsIds: Set<string> = new Set();
  #errorItemsIds: Set<string> = new Set();
  // #categoryItemMap maps a category id, with the name and icon.
  // required for the "import status" tree.
  #categoryItemMap: CategoryItemMap = new Map();
  #objectCategoryItemMap: ObjectCategoryItemMap = new Map();
  #importedCategoryData: ImportCategoryData[];

  @State() selectAllCheckboxValue: string;
  @State() selectAllCheckboxIndeterminate: boolean = false;
  @State() importStatusMessage: string;
  @State() importTreeState: TreeViewItemModel[] = [];
  @State() importedTreeFilterRegExp: RegExp;
  @Watch("importTreeState")
  importTreeStateChanged() {
    // this.#filterImportedHandler();
  }
  @State() importingIsInProcess = false;
  @State() importTreeFilterList: string[] = [];
  @State() noImport: boolean;
  @State() noObjects: boolean = true;
  @State() objectsTreeModel: TreeViewItemModel[] = [];
  @State() showHiddenImportsMessage = false;
  @State() someStatusVisible: boolean;
  @State() statusInfo: StatusInfo = {
    error: {
      display: true,
      number: 0
    },
    warning: {
      display: true,
      number: 0
    },
    success: {
      display: true,
      number: 0
    }
  };

  @Watch("importTreeState")
  watchImportTreeStateHandler() {
    this.#evaluateImport();
    if (this.noImport) {
      this.importStatusMessage =
        this.#componentLocale.main.importStatus.noImportedFiles;
    } else {
      this.importStatusMessage =
        this.#componentLocale.main.importStatus.importedButHidden;
    }
  }
  @Watch("objectsTreeModel")
  watchObjectsTreeModelHandler(newState: TreeViewItemModel[]) {
    this.noObjects = !newState || newState.length === 0;
  }

  /**
   * Callback invoked when user wants to cancel the export process.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * Callback invoked when user wants to initiate the import process.
   */
  @Prop() readonly importCallback!: ImportCallback;

  /**
   * Callback invoked when user wants to load the xpz information
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   * Displays status buttons (errors, warning, succeeded) with minimal UI (no captions)
   */
  @Prop() readonly objectContextMenuCallback!: ObjectContextMenuCallback;

  /**
   * Callback invoked when user wants to access the export options.
   */
  @Prop() readonly optionsCallback!: OptionsCallback;

  /**
   * Imported xml or xps file name
   */
  @Prop({ mutable: true }) selectedFile: File;
  @Watch("selectedFile")
  selectedFileChanged(newFile: File) {
    if (newFile) {
      this.#loadFileHandler(newFile);
    }
  }

  /**
   * Displays status buttons (error, warning, success) with minimal UI (no captions).
   */
  @Prop() readonly statusMinimal = false;

  /**
   * Emitted once just after the component is fully loaded.
   */
  @Event() componentDidLoadEvent: EventEmitter<boolean>;

  /**
   * Emitted once just after the component is fully loaded and the first render() occurs
   */
  @Event() componentDidRenderFirstTime: EventEmitter<boolean>;

  componentDidLoad() {
    this.#fileInputEl.focus();
  }

  componentDidRender() {}

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.componentDidLoadEvent.emit(true);
    // this.#evaluateObjects();
    this.#evaluateImport();
    this.importStatusMessage =
      this.#componentLocale.main.importStatus.noImportedFiles;
    this.#evaluateImportStatusMessage();
  }

  /**
   * Add the result of importing an item
   */
  @Method()
  async addResultItem(item: ImportItemResultData) {
    this.importTreeState = addImportItemResultDataItem(
      item,
      this.#categoryItemMap,
      this.#objectCategoryItemMap,
      this.importTreeState
    );
    this.statusInfo[item.status].number++;
    this.#updateStatusItemsState();
  }

  #cancelImportHandler = () => {
    this.cancelCallback();
  };

  #clearStatusHandler = () => {
    this.importTreeState = [];
    this.statusInfo.error.number = 0;
    this.statusInfo.warning.number = 0;
    this.statusInfo.success.number = 0;
  };

  #evaluateImport = () => {
    this.noImport = !!(
      this.importTreeState?.length === 0 || !this.importTreeState
    );
  };

  #evaluateImportStatusMessage = () => {
    this.someStatusVisible =
      this.statusInfo.error.display ||
      this.statusInfo.warning.display ||
      this.statusInfo.success.display;
  };

  #importObjectsHandler = async () => {
    /* first clear imported objects (this clears errors/warnings/success counts as well)*/
    this.#clearStatusHandler();
    /* then do the import*/
    this.importingIsInProcess = true;

    /*
      Process checked objects manually, considering lazy loading.  
      Example:  
      1. The "Attributes" category is lazy-loaded and marked as checked.  
      2. The user runs the import without expanding the node.  
      3. No attribute objects appear as checked, since lazy load was not triggered.  
      4. Still, all attribute objects must be included, because the category itself is checked.  

      This avoids forcing users to expand nodes just to select objects.  
      Large categories are not expanded by default to improve performance, which is crucial 
      for very large imports (1000+ objects).  
    */
    const checkedObjectsIds: string[] = [];
    for (let category of this.#objectsTreeEl.model) {
      if (category.items?.length > 0) {
        for (let obj of category.items) {
          if (obj.checked) checkedObjectsIds.push(obj.id);
        }
      } else if (category.lazy && category.checked) {
        const categoryData = this.#importedCategoryData.find(
          cat => cat.id == category.id
        );
        for (let obj of categoryData.items) {
          checkedObjectsIds.push(obj.id);
        }
      }
    }
    this.importCallback(checkedObjectsIds).then(() => {
      this.importingIsInProcess = false;
    });
  };

  #loadFileHandler = async (file: File) => {
    this.#importedCategoryData = await this.loadCallback(file);
    this.#categoryItemMap.clear();
    this.#objectCategoryItemMap.clear();

    this.#importedCategoryData.forEach(category => {
      this.#categoryItemMap.set(category.id, {
        name: category.name,
        icon: category.icon
      });
      category.items.forEach(object => {
        this.#objectCategoryItemMap.set(object.id, category.id);
      });
    });

    const importCategoryTreeData = convertImportCategoryDataToTreeView(
      this.#importedCategoryData
    );
    this.objectsTreeModel = importCategoryTreeData;
  };

  #lazyLoadTreeItems = async (treeItemId: string) => {
    const category = this.#importedCategoryData.find(
      cat => cat.id === treeItemId
    );
    return category && category.items.length > 0
      ? convertImportCategoryItemToTreeItem(category.icon, category.items)
      : [];
  };

  #objectsTreeCheckedItemsChangedHandler = (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    const allItems = event.detail;
    const checkedItemIds: string[] = [];
    const pendingCheckedItemIds: string[] = [];
    const checkedObjectsIds: string[] = [];
    allItems.forEach(item => {
      if (item.item.checked) {
        checkedItemIds.push(item.item.id);
        if (item.item.lazy && !item.item.items)
          pendingCheckedItemIds.push(item.item.id);
      }
      if (item.item.checked && item.item.metadata === KB_OBJECT) {
        checkedObjectsIds.push(item.item.id);
      }
    });

    for (let pendingCheckedItemId of pendingCheckedItemIds) {
      const category = this.#importedCategoryData.find(
        cat => cat.id === pendingCheckedItemId
      );
      if (category) {
        for (let item of category.items) {
          if (checkedObjectsIds.indexOf(item.id) < 0)
            checkedObjectsIds.push(item.id);
        }
      }
    }

    // Evaluate "Select All / Unselect All" checkbox state
    if (allItems.size === checkedItemIds.length) {
      this.selectAllCheckboxValue = CHECKBOX_ALL_CHECKED_VALUE;
      this.selectAllCheckboxIndeterminate = false;
    } else if (checkedItemIds.length === 0) {
      this.selectAllCheckboxValue = CHECKBOX_ALL_UNCHECKED_VALUE;
      this.selectAllCheckboxIndeterminate = false;
    } else {
      this.selectAllCheckboxIndeterminate = true;
    }
  };

  #objectsTreeContextMenuHandler = (
    event: CustomEvent<TreeViewItemContextMenu>
  ) => {
    if (event.detail.metadata === KB_OBJECT) {
      this.objectContextMenuCallback("imported", {
        selection: this.#selectedObjectsInFileIds,
        clientX: event.detail.contextmenuEvent.screenX,
        clientY: event.detail.contextmenuEvent.screenY
      });
    }
  };

  #objectsTreeSelectedItemsChangedHandler = (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    this.#selectedObjectsInFileIds = event.detail.map(item => item.item.id);
  };

  #optionsHandler = () => {
    if (this.optionsCallback) {
      this.optionsCallback();
      // returns boolean
    }
  };

  #removeFileSelection = () => {
    this.selectedFile = null;
    this.#fileInputHiddenEl.value = null;
  };

  #selectFileInputHandler = () => {
    this.#fileInputHiddenEl.click();
  };

  #selectFileInputChangedHandler = (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    if (target.files?.length > 0) {
      this.selectedFile = target.files[0];
    }
  };

  #statusButtonsSelectionChangedHandler = (
    event: CustomEvent<CheckedItemsInfo>
  ) => {
    const activeStates = event.detail;
    const statuses: Status[] = [];

    const errorIndex = activeStates.findIndex(
      state => state.itemId === "errors"
    );
    const warningIndex = activeStates.findIndex(
      state => state.itemId === "warnings"
    );
    const successIndex = activeStates.findIndex(
      state => state.itemId === "successes"
    );

    if (errorIndex !== -1) {
      statuses.push("error");
    }
    if (warningIndex !== -1) {
      statuses.push("warning");
    }
    if (successIndex !== -1) {
      statuses.push("success");
    }

    let pattern = `"${STATUS_REGEXP_KEY}":""`;
    if (statuses.length) {
      const statusOptions = statuses.join("|");
      pattern = `"${STATUS_REGEXP_KEY}":"(${statusOptions})"`;
    }
    this.importedTreeFilterRegExp = new RegExp(pattern, "i");

    // Update statusInfo for the status-buttons
    this.statusInfo = {
      error: {
        display: errorIndex !== -1,
        number: this.statusInfo.error.number
      },
      warning: {
        display: warningIndex !== -1,
        number: this.statusInfo.warning.number
      },
      success: {
        display: successIndex !== -1,
        number: this.statusInfo.success.number
      }
    };

    this.#evaluateImportStatusMessage();
  };

  #toggleSelectionClickHandler = (
    event: ChCheckboxCustomEvent<any> | InputEvent
  ) => {
    if (this.#objectsTreeEl) {
      const checked = event.detail === CHECKBOX_ALL_CHECKED_VALUE;
      this.#objectsTreeEl.updateAllItemsProperties({ checked: checked });
    }
  };

  #updateStatusItemsState = () => {
    this.#errorItemsIds.clear();
    this.#warningItemsIds.clear();
    this.#successItemsIds.clear();
    this.importTreeState.forEach(item => {
      const items = item.items;
      items.forEach(child => {
        if (child.metadata === "error") {
          this.#errorItemsIds.add(child.id);
        }
        if (child.metadata === "warning") {
          this.#warningItemsIds.add(child.id);
        }
        if (child.metadata === "success") {
          this.#successItemsIds.add(child.id);
        }
      });
    });
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <section class="section">
          <header
            // header
            class="field-group dialog-header-with-border header header-two-cols spacing-body-block-start spacing-body-inline"
            slot="header"
          >
            <div
              // select a file
              class="field field-inline"
            >
              <label class="label" htmlFor="file-name">
                {this.#componentLocale.header.fileNameLabel}
              </label>
              <ch-edit
                autoFocus
                id="file-name"
                class="input"
                part="file-name"
                type="text"
                readonly
                value={this.selectedFile?.name}
                placeholder={this.#componentLocale.header.fileNamePlaceholder}
                startImgSrc={FILE_ICON}
                ref={(el: HTMLChEditElement) =>
                  (this.#fileInputEl = el as HTMLChEditElement)
                }
              ></ch-edit>
              <input
                hidden
                type="file"
                accept=".xpz, .xml"
                onChange={this.#selectFileInputChangedHandler}
                ref={(el: HTMLInputElement) =>
                  (this.#fileInputHiddenEl = el as HTMLInputElement)
                }
              />
            </div>

            <div class="buttons-spacer">
              <button
                // remove file selection
                aria-label={this.#componentLocale.header.removeFileSelection}
                title={this.#componentLocale.header.removeFileSelection}
                id="reset-all-button"
                class="button-tertiary button-icon-only"
                part="reset-all-button"
                onClick={this.#removeFileSelection}
              >
                <ch-image class="icon-m" src={MENU_DELETE}></ch-image>
              </button>

              <button
                // select file
                id="select-file-load-button"
                class="button-primary"
                part="select-file-load-button"
                onClick={this.#selectFileInputHandler}
                disabled={this.importingIsInProcess}
              >
                {this.#componentLocale.header.selectFileButton}
              </button>
            </div>
          </header>

          <section
            // objects in file
            class="objects-in-file__section"
          >
            <h2 class="dialog-header dialog-header-with-border dialog-header__objects-in-file subtitle-regular-xs	 text-align-center spacing-body-inline">
              {this.#componentLocale.main.objectsInFile.title}
            </h2>

            <div class="field-group objects-in-file__main">
              {!this.noObjects ? (
                <ch-tree-view-render
                  // objects in file tree
                  showLines="last"
                  class="tree-view tree-view-import"
                  model={this.objectsTreeModel}
                  dragDisabled={true}
                  dropDisabled={true}
                  toggleCheckboxes={true}
                  checkbox={true}
                  checked={true}
                  lazyLoadTreeItemsCallback={this.#lazyLoadTreeItems}
                  onCheckedItemsChange={
                    this.#objectsTreeCheckedItemsChangedHandler
                  }
                  onSelectedItemsChange={
                    this.#objectsTreeSelectedItemsChangedHandler
                  }
                  onItemContextmenu={this.#objectsTreeContextMenuHandler}
                  ref={(el: HTMLChTreeViewRenderElement) =>
                    (this.#objectsTreeEl = el as HTMLChTreeViewRenderElement)
                  }
                ></ch-tree-view-render>
              ) : (
                <gx-ide-empty-state
                  isAnimated={false}
                  stateTitle={
                    this.#componentLocale.main.objectsInFile.noObjects
                  }
                  key="no-objects-empty-state"
                >
                  <button
                    // begin by selecting a file
                    class="button-secondary"
                    onClick={this.#selectFileInputHandler}
                  >
                    {this.#componentLocale.main.objectsInFile.selectFile}
                  </button>
                </gx-ide-empty-state>
              )}

              <footer class="field-group objects-in-file__footer spacing-body-inline">
                <ch-checkbox
                  // select all / unselect all checkbox
                  id="select-all-checkbox"
                  class="checkbox create-data-in-kb"
                  checkedValue={CHECKBOX_ALL_CHECKED_VALUE}
                  unCheckedValue={CHECKBOX_ALL_UNCHECKED_VALUE}
                  caption={
                    this.#componentLocale.main.objectsInFile.selectUnselect
                  }
                  value={this.selectAllCheckboxValue}
                  onInput={this.#toggleSelectionClickHandler}
                  disabled={
                    this.importingIsInProcess ||
                    this.objectsTreeModel.length === 0
                  }
                  part="select-all-checkbox"
                ></ch-checkbox>

                <div class="buttons-spacer">
                  <button
                    // import objects button
                    id="import-btn"
                    class="button-primary "
                    onClick={this.#importObjectsHandler}
                    disabled={
                      this.objectsTreeModel?.length === 0 ||
                      this.importingIsInProcess
                    }
                  >
                    {this.#componentLocale.main.objectsInFile.importButton}
                  </button>

                  <button
                    // cancel-import button
                    id="cancel-import-btn"
                    class="button-secondary "
                    onClick={this.#cancelImportHandler}
                    disabled={!this.importingIsInProcess}
                    part="cancel-import-button"
                  >
                    {this.#componentLocale.main.objectsInFile.cancelButton}
                  </button>

                  <button
                    // settings/options button
                    id="select-kb-btn"
                    class="button-tertiary button-icon-only"
                    part="select-kb-btn"
                    onClick={this.#optionsHandler}
                  >
                    <ch-image
                      class="icon-m"
                      src={GEMINI_TOOLS_SETTINGS}
                    ></ch-image>
                  </button>
                </div>
              </footer>
            </div>
          </section>

          <section
            // import status
            class="import-status__section"
          >
            <h2 class="dialog-header dialog-header-with-border dialog-header__import-status subtitle-regular-xs	 text-align-center spacing-body-inline">
              {this.#componentLocale.main.importStatus.title}
            </h2>

            <div class="field-group import-status__main">
              {!this.noImport && this.someStatusVisible ? (
                <ch-tree-view-render
                  // import status tree
                  model={this.importTreeState}
                  toggleCheckboxes={true}
                  showLines="last"
                  class="tree-view tree-view-import"
                  filter={this.importedTreeFilterRegExp}
                  filterType="metadata"
                  filterOptions={{
                    autoExpand: true,
                    hideMatchesAndShowNonMatches: false
                  }}
                ></ch-tree-view-render>
              ) : (
                <gx-ide-empty-state
                  isAnimated={false}
                  stateIconSrc={NOTICE_ICON}
                  stateTitle={this.importStatusMessage}
                  key="no-objects-empty-state"
                ></gx-ide-empty-state>
              )}

              <footer class="import-status__footer spacing-body-inline">
                <button
                  // clear status button
                  class="button-secondary import-clear-status-btn"
                  onClick={this.#clearStatusHandler}
                  disabled={this.noImport || this.importingIsInProcess}
                  part="clear-status-btn"
                >
                  {this.#componentLocale.main.importStatus.clearButton}
                </button>
              </footer>
            </div>
          </section>

          <footer
            // footer
            class="footer"
          >
            <gx-ide-status-buttons
              errors={this.statusInfo.error.number}
              warnings={this.statusInfo.warning.number}
              successes={this.statusInfo.success.number}
              hideMessage
              compact
              minimal={this.statusMinimal}
              onSelectionChanged={this.#statusButtonsSelectionChangedHandler}
            ></gx-ide-status-buttons>
          </footer>
        </section>
      </Host>
    );
  }
}
export type CancelCallback = () => Promise<boolean>;

export type ImportCallback = (itemIds: string[]) => Promise<boolean>;

export type ImportItemData = {
  id: string;
  name: string;
};

export type LoadCallback = (file: File) => Promise<ImportCategoryData[]>;

export type ObjectContextMenuCallback = (
  tree: ObjectsSourceType,
  contextMenuInfo: ContextMenuInfo
) => Promise<void>;

export type ObjectsSourceType = "in-file" | "imported";

export type OptionsCallback = () => Promise<void>;

export interface SelectedObject {
  itemId: string;
  subItemsIds: string[];
}

export type StatusItemsState = {
  error: string[];
  warning: string[];
  success: string[];
};

type StatusInfo = {
  error: {
    display: boolean;
    number: number;
  };
  warning: {
    display: boolean;
    number: number;
  };
  success: {
    display: boolean;
    number: number;
  };
};

export type CategoryItemMap = Map<string, CategoryItemMapValue>;
export type CategoryItemMapValue = { name: string; icon: string };
export type ObjectCategoryItemMap = Map<string, string>;
export type Status = "success" | "error" | "warning";
